import clsx from 'clsx'
import { Folder, Document, AlertErrorCircle, Retry } from '@/assets/icons'
import { CircularProgressContinuousSized } from '@/components/loaders/CircularProgressContinuous'
import { IconButton } from '@/components/icon-button/IconButton'
import { DriveListItemStatus } from '@/user_files/drive/constants'

export type DriveListItemVariant = 'folder' | 'file'

export interface DriveListItemProps {
  /** Folder or file */
  variant: DriveListItemVariant
  /** Status */
  status?: DriveListItemStatus
  /** Name */
  name: string
  /** Formatted date segment. */
  formattedDate: string
  /** Formatted time segment. */
  formattedTime: string
  /** Secondary details for the row (e.g. "24 KB" or "4 files"). */
  detailsLabel?: string
  /** Shown below name when status is Failed (with icon). */
  errorMessage?: string
  /** Disable the checkbox. */
  disabled?: boolean
  /** Whether the item is selected. */
  selected?: boolean
  /** On toggle select */
  onToggleSelect?: () => void
  /** On retry */
  onRetry?: () => void
  /** On click */
  onClick?: () => void
  /** Optional icon override for file rows. */
  fileIcon?: React.ReactNode
  /** Overflow menu */
  overflowMenu: React.ReactNode
}

const PLACEHOLDER_VALUE = '--'

/**
 * Shared table row for drive list items (folders and files).
 * Supports folder/file variants and status states: PENDING, PROCESSING, COMPLETED, FAILED.
 */
export function DriveListItem({
  variant,
  status = DriveListItemStatus.Completed,
  name,
  formattedDate,
  formattedTime,
  detailsLabel,
  errorMessage,
  disabled = false,
  selected,
  onToggleSelect,
  onRetry,
  onClick,
  fileIcon,
  overflowMenu,
}: DriveListItemProps) {
  const isLoading = status === DriveListItemStatus.Processing || status === DriveListItemStatus.Pending
  const isError = status === DriveListItemStatus.Failed
  const isCheckboxDisabled = disabled || isLoading
  const displayDate = isLoading ? PLACEHOLDER_VALUE : formattedDate
  const displayTime = isLoading ? '' : formattedTime
  const isRowInteractive = typeof onClick === 'function'

  const handleRowKeyDown = (e: React.KeyboardEvent<HTMLTableRowElement>) => {
    if (!isRowInteractive) return

    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      onClick()
    }
  }

  const renderIcon = () => {
    if (isLoading) {
      return <CircularProgressContinuousSized size={24} thickness={5} showTrack className="shrink-0" />
    }
    if (variant === 'folder') {
      return <Folder className="w-6 h-6 shrink-0 text-brand-neutral-950" />
    }
    if (fileIcon) return fileIcon
    return <Document className="w-6 h-6 shrink-0 text-brand-neutral-950" />
  }

  return (
    <tr
      className={clsx(
        'h-[54px] border-t border-brand-neutral-200 first:border-t-0',
        isRowInteractive && 'cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-inset',
        isError ? 'bg-red-50' : 'hover-capable:hover:bg-brand-neutral-100'
      )}
      onClick={onClick}
      onKeyDown={handleRowKeyDown}
      tabIndex={isRowInteractive ? 0 : undefined}
    >
      <td
        className="w-12 align-middle pl-4 pr-3 md:pl-2"
        onClick={(e) => {
          e.stopPropagation()
        }}
      >
        <input
          type="checkbox"
          checked={selected && !isCheckboxDisabled}
          onChange={isCheckboxDisabled ? undefined : onToggleSelect}
          disabled={isCheckboxDisabled}
          className={clsx(
            'size-5 rounded border-brand-neutral-300 text-brand-500',
            isCheckboxDisabled ? 'cursor-not-allowed disabled:bg-brand-neutral-100' : 'cursor-pointer focus:ring-brand-500'
          )}
          aria-label={`Select ${name}`}
        />
      </td>
      <td className="align-middle pl-0 pr-6">
        <div className="flex items-center gap-2 min-w-0">
          {renderIcon()}
          <div className="min-w-0 flex-1 flex flex-col gap-0.5">
            <span className={clsx('truncate', isLoading ? 'text-brand-neutral-500' : 'text-brand-neutral-950')} title={name}>
              {name}
            </span>
            {isError && errorMessage && (
              <div className="flex items-center gap-1 min-w-0">
                <AlertErrorCircle className="w-3.5 h-3.5 shrink-0 text-red-800" aria-hidden />
                <span className="text-xs text-red-800 truncate" title={errorMessage}>
                  {errorMessage}
                </span>
              </div>
            )}
          </div>
        </div>
      </td>
      <td
        className={clsx(
          'hidden min-w-0 overflow-hidden px-6 align-middle [@container_(min-width:_640px)]:table-cell',
          isLoading ? 'text-brand-neutral-500' : 'text-brand-neutral-800'
        )}
      >
        {displayDate || displayTime ? (
          <span className="block truncate tabular-nums" title={[displayDate, displayTime].filter(Boolean).join(' ')}>
            {[displayDate, displayTime].filter(Boolean).join(' ')}
          </span>
        ) : null}
      </td>
      <td
        className={clsx(
          'hidden min-w-0 overflow-hidden px-6 align-middle [@container_(min-width:_768px)]:table-cell',
          isLoading ? 'text-brand-neutral-500' : 'text-brand-neutral-800'
        )}
      >
        <span className="block truncate tabular-nums" title={detailsLabel ?? PLACEHOLDER_VALUE}>
          {detailsLabel ?? PLACEHOLDER_VALUE}
        </span>
      </td>
      <td
        className="w-12 pl-2 pr-2 align-middle"
        onClick={(e) => {
          e.stopPropagation()
        }}
      >
        <div className="flex min-h-7 min-w-7 items-center justify-end">
          {isError && onRetry && (
            <IconButton
              type="button"
              variant="plain"
              color="neutral"
              size="sm"
              icon={<Retry className="w-5 h-5" />}
              aria-label="Retry"
              onClick={(e) => {
                e.stopPropagation()
                onRetry()
              }}
            />
          )}
        </div>
      </td>
      <td
        className="pl-0 pr-2 align-middle md:pr-0"
        onClick={(e) => {
          e.stopPropagation()
        }}
      >
        <div className="flex min-h-7 min-w-7 items-center justify-end">{overflowMenu}</div>
      </td>
    </tr>
  )
}
